'use client';

import React from 'react';
import { Trophy } from 'lucide-react';
import type { PrizeDistributionEntry, PrizePoolCurrency } from '../interfaces/tournaments';
import {
  formatPrizePool,
  getPrizePositionLabel,
  hasPrizeDistribution,
} from '../interfaces/tournaments';
import { cn } from '@/lib/utils';

interface PrizeDistributionDisplayProps {
  distribution?: PrizeDistributionEntry[] | null;
  totalPrize?: number | string | null;
  currency?: PrizePoolCurrency | null;
  className?: string;
}

export const PrizeDistributionDisplay: React.FC<PrizeDistributionDisplayProps> = ({
  distribution,
  totalPrize,
  currency = 'USD',
  className,
}) => {
  if (!hasPrizeDistribution(distribution)) return null;

  const sorted = (distribution ?? []).toSorted((a, b) => a.position - b.position);
  const distributed = sorted.reduce((sum, entry) => sum + Number(entry.amount), 0);
  const total = Number(totalPrize ?? 0);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2">
        <Trophy className="h-4 w-4 text-primary" />
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
          Distribución de premios
        </p>
      </div>

      <div className="rounded-xl border border-primary/15 bg-card/50 overflow-hidden divide-y divide-primary/10">
        {sorted.map((entry) => (
          <div
            key={`${entry.position}-${entry.amount}`}
            className="flex items-center justify-between gap-4 px-4 py-3"
          >
            <span className="text-sm text-muted-foreground">
              {getPrizePositionLabel(entry.position, entry.label)}
            </span>
            <span className="text-sm font-semibold text-foreground">
              {formatPrizePool(entry.amount, currency)}
            </span>
          </div>
        ))}
      </div>

      {total > 0 && (
        <p className="text-xs text-muted-foreground">
          Distribuido: {formatPrizePool(distributed, currency)}
          {distributed < total && (
            <span> · Restante: {formatPrizePool(total - distributed, currency)}</span>
          )}
        </p>
      )}
    </div>
  );
};

export default PrizeDistributionDisplay;
